import { Box, Stack, Typography } from '@mui/material';
import { useTheme, SxProps, Theme } from '@mui/material/styles';
import _ from 'lodash';
import { COLOR_OPTIONS } from 'types';

interface StatusSegmentBarSection {
  key: string;
  label: string;
  value: number;
  color: COLOR_OPTIONS | 'grey';
}

interface StatusSegmentBarProps {
  sections: StatusSegmentBarSection[];
  height?: number;
  showLabels?: boolean;
  showTotal?: boolean;
  sx?: SxProps<Theme>;
  gap?: number;
}

const StatusSegmentBar = ({
  sections,
  height = 24,
  showLabels = false,
  showTotal = true,
  sx = {},
  gap = 1,
}: StatusSegmentBarProps) => {
  const theme = useTheme();
  const total = sections.reduce((sum, section) => sum + section.value, 0);

  if (total === 0) return null;

  const blocks = sections.flatMap((section) =>
    Array.from({ length: section.value }, () => ({
      ...section,
      width: (1 / total) * 100,
    }))
  );

  return (
    <Stack spacing={1} sx={sx}>
      {showLabels && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {sections.map((section) => (
              <Typography
                key={section.key}
                variant="body1"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '&::before': {
                    content: '""',
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    marginRight: '4px',
                    bgcolor: _.get(theme.palette, [section.color, 'main']),
                  },
                }}
              >
                {section.label}: {section.value}
              </Typography>
            ))}
          </Box>
          {showTotal && (
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
              <Typography variant="h4">
                Total: {total.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Stack>
      )}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            height,
            borderRadius: 1,
            gap: `${gap}px`,
            bgcolor: theme.palette.grey[100],
            p: `${gap}px`,
          }}
        >
          {blocks.map((block, index) => (
            <Box
              key={`${block.key}-${index}`}
              sx={{
                width: `${block.width}%`,
                bgcolor: _.get(theme.palette, [block.color, 'main']),
                transition: 'width 0.3s ease',
                minWidth: '2px',
                borderRadius: 0.5,
              }}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default StatusSegmentBar;
